// Abonent moduli — RESELLER uchun. Kontrakt manbai:
// `SubscriberController`, `GrantController` (xsib-subscriber) va
// `SessionControlResource` (xsib-session).
//
// =====================================================================
// ⚠️ BU QATLAM ADMIN PANELIDAGI `features/subscriber/subscriberApi.ts`
// NING KESIMI EMAS
// =====================================================================
// Egasi qarori (11-SHERIK-KABINETI-UI.md §10.5): sherik moduli
// SODDALASHTIRILADI va o'z yo'li bilan boradi. Shuning uchun bu yerda
// admin faylining 669 qatoridan atigi kerak bo'lgani bor va farqlar
// ATAYLAB:
//
//   · `provision` (abonent + paket + pul bitta amalda) YO'Q — u
//     `payment.create` talab qiladi va bu huquq `PartnerKind` ning
//     IKKALA statik to'plamida ham yo'q, ya'ni tugma har safar 403
//     bilan qaytardi (ADR-029 §2);
//   · `issueGrant` `Idempotency-Key` YUBORADI — admin nusxasi uni
//     umuman yubormaydi va bugungi backend kalitsiz so'rovni 400 bilan
//     RAD ETADI;
//   · `DELETE /api/subscribers/{id}` YO'Q — pastdagi `closeSubscriber`
//     izohiga qarang.
//
// `features/*` bir-birini import qilmaydi (07-FRONTEND.md §6), shuning
// uchun kassa moduli bilan umumiy bo'lgan yagona narsa — `shared/`.

import axios from 'axios'
import { z } from 'zod'
import { api } from '@/shared/api/client'
import { operationResultSchema, type OperationResult } from '@/shared/api/operationResult'
import {
  instantSchema,
  moneySchema,
  planSellable,
  toMoney,
  uuidSchema,
} from '@/shared/api/schemas'
import { money, type Money } from '@/shared/lib/money'
import type { SubscriberState } from '@/shared/ui/StatusTag'
// ⚠️ FAQAT TIP: `contactDraft.ts` shu fayldan `normalizePhone` ni oladi,
// ya'ni oddiy import aylana yasardi. `import type` kompilyatsiyada
// yo'qoladi va ish vaqtida hech qanday bog'liqlik qolmaydi.
import type { ContactRequest } from './contactDraft'
import {
  subscriberProfileSchema,
  type SubscriberProfile,
  type SubscriberProfileDraft,
} from './profileApi'

export const SUBSCRIBER_STATES = ['NEW', 'ACTIVE', 'SUSPENDED', 'BLOCKED', 'CLOSED'] as const

/**
 * Ulanish usuli — `SubscriberDraft.kind` backendda `@NotNull`.
 *
 * ⚠️ Formadan OLIB TASHLAB, `PPPOE` deb qotirib qo'yish mumkin emasdi:
 * bu taxmin bo'lardi va noto'g'ri tanlangan usul abonentni umuman
 * ulanmaydigan qilib qo'yadi — nosozlik esa faqat mijoz qo'ng'iroq
 * qilganda ko'rinadi.
 */
export const DELIVERY_METHODS = ['PPPOE', 'HOTSPOT', 'STATIC'] as const
export type DeliveryMethod = (typeof DELIVERY_METHODS)[number]

export const GRANT_STATES = ['PENDING', 'ACTIVE', 'EXHAUSTED', 'EXPIRED', 'REVOKED'] as const

/**
 * Login naqshi — `SubscriberDraft` dagi `@Pattern` ning AYNAN o'zi.
 *
 * ⚠️ TAKROR ATAYLAB (admin nusxasidagi bilan bir xil sabab): server rad
 * javobi modalning TEPASIDA chiqadi, maydonning yonida emas, va
 * operator qaysi maydonda xato borligini qidiradi. Bu yerda tekshiruv
 * maydonning ostida, yozilayotgan paytda ko'rinadi.
 */
export const LOGIN_PATTERN = /^[A-Za-z0-9._@-]{1,64}$/

/**
 * `uz.xsib.common.contact.Phones.E164`.
 *
 * ⚠️ MAMLAKATGA BOG'LANMAGAN: `+998` va `+93` — ikkalasi ham ishlaydigan
 * tenantlar, qattiq `^\+998` afg'on abonentini butunlay to'sib qo'yardi.
 */
export const PHONE_E164 = /^\+[1-9][0-9]{7,14}$/

/**
 * Raqamni serverga yuborishdan oldin tozalaydi.
 *
 * Veb-sahifadan yoki hujjatdan nusxa olingan raqamda uzilmas probel va
 * turli tirelar uchraydi; tozalanmasa raqam E.164 tekshiruvidan
 * o'tmaydi va forma «telefon noto'g'ri» deb turib oladi — kiritilgan
 * raqam esa ko'zga to'g'ri ko'rinadi.
 */
export function normalizePhone(raw: string | null | undefined): string | null {
  if (raw == null) return null
  // ⚠️ Belgilar ESCAPE bilan yozilgan, ko'rinishi bilan emas: uzilmas
  // probel va turli tirelar ekranda oddiylaridan farq qilmaydi, ya'ni
  // kodni o'qigan odam ularni ko'rmaydi va tasodifan o'chirib qo'yishi
  // mumkin (eslint no-irregular-whitespace ham shuni ushlaydi).
  const cleaned = raw.trim().replace(/[\s\u00A0()\-\u2010-\u2015.]/g, '')
  if (!cleaned) return null
  return cleaned.startsWith('00') ? `+${cleaned.slice(2)}` : cleaned
}

// --- Sxemalar ---------------------------------------------------------------

const subscriberSchema = z.object({
  id: uuidSchema,
  login: z.string(),
  displayName: z.string().nullish(),
  phone: z.string().nullish(),
  kind: z.enum(DELIVERY_METHODS),
  state: z.enum(SUBSCRIBER_STATES),
  createdAt: instantSchema.nullish(),
  profile: subscriberProfileSchema.nullish(),
  branchNodeId: uuidSchema.nullish(),
})

export interface Subscriber {
  id: string
  login: string
  displayName: string | null
  phone: string | null
  kind: DeliveryMethod
  state: SubscriberState
  createdAt: string | null
  profile: SubscriberProfile | null
  branchNodeId: string | null
}

function toSubscriber(raw: z.infer<typeof subscriberSchema>): Subscriber {
  return {
    id: raw.id,
    login: raw.login,
    displayName: raw.displayName ?? null,
    phone: raw.phone ?? null,
    kind: raw.kind,
    state: raw.state,
    createdAt: raw.createdAt ?? null,
    profile: raw.profile ?? null,
    branchNodeId: raw.branchNodeId ?? null,
  }
}

const pageRowSchema = z.object({
  subscriber: subscriberSchema,
  balanceMinor: z.number().nullish(),
  currency: z.string(),
  /** Backend `SubscriberPage.SubscriberRow.planName` — KANONIK nom */
  planName: z.string().nullish(),
  expiresAt: instantSchema.nullish(),
})

const pageSchema = z.object({
  rows: z.array(pageRowSchema),
  total: z.number(),
  page: z.number(),
  size: z.number(),
})

export interface SubscriberRow {
  subscriber: Subscriber
  /**
   * ⚠️ `null` = NOMA'LUM, nol EMAS. `?? 0` yozish ledger javob
   * bermaganda butun ro'yxatni «balansi nol» ga aylantirardi va bu
   * ishonarli ko'rinardi — reseller esa aynan shu songa qarab
   * mijoziga qo'ng'iroq qiladi.
   */
  balance: Money | null
  /** Amaldagi paketning kanonik nomi; `null` = faol paket yo'q */
  planName: string | null
  expiresAt: string | null
}

export interface SubscriberPage {
  rows: SubscriberRow[]
  total: number
}

// --- Ro'yxat ----------------------------------------------------------------

export interface SubscriberListQuery {
  search: string
  /** `null` — holat bo'yicha filtr yo'q */
  state: string | null
  page: number
  size: number
}

/**
 * Abonentlar ro'yxati — doira SERVER tomonida (`SUBTREE`, ADR-029 §2).
 *
 * ⚠️ Klientda `resellerId` FILTRI YO'Q va qo'shilmaydi. `AccessGuard`
 * har so'rovda predikat qo'yadi; panelda uni takrorlash ikkinchi
 * haqiqat manbai bo'lardi va ular ajrashganda ekran serverdan KAM
 * yoki KO'P ko'rsatardi (04-ROLES.md §5).
 *
 * ⚠️ `includeClosed` HOLAT FILTRIDAN kelib chiqadi, alohida belgi emas.
 * Backend standarti `true`, ya'ni yopilgan hisoblar ro'yxatning
 * boshini egallardi va reseller ular orasidan tirik mijozni qidirardi.
 * Uchinchi boshqaruv elementi qo'shish o'rniga qoida bitta: yopilganini
 * KO'RISH uchun uni ataylab tanlash kerak.
 */
export async function fetchSubscribers(
  query: SubscriberListQuery,
  signal: AbortSignal,
): Promise<SubscriberPage> {
  const search = query.search.trim()
  const response = await api.get<unknown>('/api/subscribers', {
    signal,
    params: {
      ...(search ? { search } : {}),
      ...(query.state ? { state: query.state } : {}),
      includeClosed: query.state === 'CLOSED',
      page: query.page,
      size: query.size,
    },
  })
  const parsed = pageSchema.parse(response.data)
  return {
    total: parsed.total,
    rows: parsed.rows.map((row) => ({
      subscriber: toSubscriber(row.subscriber),
      balance: row.balanceMinor == null ? null : money(row.balanceMinor, row.currency),
      planName: row.planName ?? null,
      expiresAt: row.expiresAt ?? null,
    })),
  }
}

// --- Kartochka --------------------------------------------------------------

const overviewSchema = z.object({
  balance: moneySchema.nullish(),
  remainingBytes: z.number().nullish(),
  remainingSeconds: z.number().nullish(),
  /** Amalda qo'llanayotgan tezlik — tarifdan yoki qo'lda qo'yilgani */
  rateLimit: z.string().nullish(),
  /** Qo'lda qo'yilgan ustki qiymat; `null` — tarifniki ishlaydi */
  rateLimitOverride: z.string().nullish(),
  accessUntil: instantSchema.nullish(),
  hasUsableGrant: z.boolean().default(false),
  activeSessions: z.number(),
  /**
   * Faol sessiya tarif o'zgarishidan OLDIN boshlangan — ya'ni bazadagi
   * holat bilan tarmoqdagi holat mos emas. Sessiyani uzish tugmasining
   * sababi aynan shu.
   */
  staleSession: z.boolean(),
})

export interface SubscriberOverview {
  balance: Money | null
  remainingBytes: number | null
  remainingSeconds: number | null
  rateLimit: string | null
  rateLimitOverride: string | null
  accessUntil: string | null
  hasUsableGrant: boolean
  activeSessions: number
  staleSession: boolean
}

/**
 * Kartochkaning to'liq manzarasi — BITTA so'rovda.
 *
 * ⚠️ Balans, tezlik va sessiya alohida so'ralmaydi: backend ularni
 * ataylab bitta `subscriber.read` ostida beradi
 * (`SubscriberController.overview` javadoc'i), aks holda har biri O'Z
 * huquqini talab qilardi va kartochka yarim bo'sh chiqardi — sababi
 * ko'rinmasdan.
 */
export async function fetchOverview(id: string): Promise<SubscriberOverview> {
  const response = await api.get<unknown>(`/api/subscribers/${id}/overview`)
  const raw = overviewSchema.parse(response.data)
  return {
    balance: raw.balance == null ? null : toMoney(raw.balance),
    remainingBytes: raw.remainingBytes ?? null,
    remainingSeconds: raw.remainingSeconds ?? null,
    rateLimit: raw.rateLimit ?? null,
    rateLimitOverride: raw.rateLimitOverride ?? null,
    accessUntil: raw.accessUntil ?? null,
    hasUsableGrant: raw.hasUsableGrant,
    activeSessions: raw.activeSessions,
    staleSession: raw.staleSession,
  }
}

const accessGrantSchema = z.object({
  id: uuidSchema,
  planId: uuidSchema.nullish(),
  quotaBytes: z.number().nullish(),
  consumedBytes: z.number(),
  quotaSeconds: z.number().nullish(),
  consumedSeconds: z.number(),
  state: z.enum(GRANT_STATES),
  expiresAt: instantSchema.nullish(),
})

export interface ActiveGrant {
  id: string
  planId: string | null
  /** `null` — hajm cheklovi yo'q (limitsiz paket) */
  remainingBytes: number | null
  /** `null` — vaqt cheklovi yo'q */
  remainingSeconds: number | null
  state: (typeof GRANT_STATES)[number]
  expiresAt: string | null
}

const grantOverviewSchema = z.object({
  usable: z.array(accessGrantSchema),
  /** Tezlikni AYNAN qaysi grant boshqarayotgani */
  governingGrantId: uuidSchema.nullish(),
  accessUntil: instantSchema.nullish(),
})

export interface GrantOverview {
  usable: ActiveGrant[]
  governingGrantId: string | null
}

/**
 * Amaldagi paketlar — BARCHASI, bittasi emas.
 *
 * ⚠️ `GET /api/grants/active` (bitta grant) ATAYLAB ishlatilmadi:
 * bir abonentda bir necha faol paket bo'lishi normal va tezlik ular
 * ichidan BOSHQA qoida bo'yicha tanlanadi (`GrantController.current`
 * javadoc'i). Bittasini ko'rsatish ekranda «abonentning tarifi» degan
 * yolg'on beradi — jonli tizimda aynan shunday bo'lgan.
 */
export async function fetchGrants(subscriberId: string): Promise<GrantOverview> {
  const response = await api.get<unknown>('/api/grants/current', { params: { subscriberId } })
  const raw = grantOverviewSchema.parse(response.data)
  return {
    governingGrantId: raw.governingGrantId ?? null,
    usable: raw.usable.map((g) => ({
      id: g.id,
      planId: g.planId ?? null,
      remainingBytes: g.quotaBytes == null ? null : Math.max(0, g.quotaBytes - g.consumedBytes),
      remainingSeconds:
        g.quotaSeconds == null ? null : Math.max(0, g.quotaSeconds - g.consumedSeconds),
      state: g.state,
      expiresAt: g.expiresAt ?? null,
    })),
  }
}

// --- Paketlar katalogi ------------------------------------------------------

const planSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  nameI18n: z.record(z.string(), z.string()).nullish(),
  price: moneySchema,
  state: z.string(),
  sellable: z.boolean().nullish(),
})

export interface PlanOption {
  id: string
  /** KANONIK nom — tarjima yo'q bo'lganda shu ko'rinadi */
  name: string
  nameI18n: Record<string, string>
  price: Money
  /** Hozir sotish mumkinmi — qaror backendniki (`Plan.isSellable()`) */
  sellable: boolean
}

/**
 * Paketlar katalogi (`plan.read`).
 *
 * ⚠️ NEGA `features/cashdesk/cashdeskPlans.ts` QAYTA ISHLATILMADI:
 * `features/*` bir-birini import qilmaydi (07-FRONTEND.md §6) va ikki
 * ekranga ikki xil shakl kerak — kassa `nom → narx` xaritasini yasaydi
 * (uning ro'yxatida `planId` yo'q), bu yerda esa `id → nom` kerak,
 * chunki amaldagi grant faqat `planId` beradi. Umumiy bo'lgani —
 * endpoint va `planSellable` qoidasi, va ikkalasi ham `shared/` da.
 */
export async function fetchPlans(): Promise<PlanOption[]> {
  const response = await api.get<unknown>('/api/catalog/plans')
  return z
    .array(planSchema)
    .parse(response.data)
    .map((raw) => ({
      id: raw.id,
      name: raw.name,
      nameI18n: raw.nameI18n ?? {},
      price: toMoney(raw.price),
      sellable: planSellable(raw),
    }))
}

/**
 * Shu filial abonentiga sherik sota olmaydigan paketlar (ADR-047) — ro'yxatdan yashiriladi; server baribir tekshiradi.
 * Sotuvchi (agent tuguni) catalog'da tokendan aniqlanadi.
 */
export async function fetchDeniedPlans(branchId: string | null): Promise<ReadonlySet<string>> {
  const response = await api.get<unknown>('/api/catalog/plans/denied', { params: branchId ? { branchId } : {} })
  return new Set(z.array(z.string()).parse(response.data))
}

// --- Yaratish va tahrirlash -------------------------------------------------

/**
 * Formadan serverga ketadigan maydonlar — ATAYLAB QISQA.
 *
 * <h2>⚠️ ADMIN FORMASIDAN NIMA OLINMADI VA NEGA</h2>
 * `agentId` / `resellerId` / `ownerPartyId` — `SubscriberOwnership`
 * ularni chaqiruvchining O'Z tugunidan to'ldiradi, tahrirlashda esa
 * faqat TEKSHIRADI (to'ldirish ustki resellerga ost-resellerning
 * abonentini bir tahrir bilan o'ziga ko'chirib olish imkonini
 * berardi). Ya'ni maydonni ko'rsatish resellerga o'zi tanlay
 * olmaydigan, xato tanlaganda esa 404 beradigan tanlov qo'yardi.
 *
 * `venueId` — ob'ekt (venue) xodim tomonidagi tushuncha; sherik
 * to'plamida unga tegishli birorta huquq yo'q.
 *
 * `simultaneousUse` — bo'sh qoldirilsa tarifdan olinadi, ya'ni to'g'ri
 * standart ALLAQACHON bor. Maydon esa «nechta qurilma» degan savolni
 * har yaratishda majburiy qilardi.
 *
 * `staticIp` — manzil rejasini talab qiladi. Ixtiyoriy yozilgan IP
 * tarmoqda to'qnashuv yasaydi va uni faqat muhandis yechadi; nosozlik
 * esa BOSHQA abonentda ko'rinadi.
 *
 * Uchalasi ham keyinchalik qo'shilishi mumkin (egasi qarori §10.5) —
 * lekin ularning har biri o'z tushuntirishi bilan kelishi kerak.
 */
export interface SubscriberDraft {
  login: string
  /** ⚠️ FAQAT yaratishda. Tahrirda parol alohida amal — pastdagi izoh */
  password?: string
  displayName?: string | null
  phone?: string | null
  kind: DeliveryMethod
  /** Yaratishda to'liq, tahrirlashda faqat to'ldirilgani — server qolganini saqlaydi */
  profile?: SubscriberProfileDraft | null
}

export async function createSubscriber(draft: SubscriberDraft): Promise<Subscriber> {
  const response = await api.post<unknown>('/api/subscribers', draft)
  return toSubscriber(subscriberSchema.parse(response.data))
}

/**
 * Tahrirlash.
 *
 * ⚠️ `password` YUBORILMAYDI. `PUT /api/subscribers/{id}` ni
 * `subscriber.update` ochadi, parolni esa ATAYLAB alohida va XAVFLI
 * huquq (`subscriber.password.set`) ochadi: profilni tahrirlash va
 * abonent hisobini egallab olish bir xil ishonch darajasi emas
 * (`SubscriberController.changePassword` javadoc'i).
 */
export async function updateSubscriber(
  id: string,
  draft: SubscriberDraft,
): Promise<Subscriber> {
  const response = await api.put<unknown>(`/api/subscribers/${id}`, draft)
  return toSubscriber(subscriberSchema.parse(response.data))
}

// --- Kontakt ----------------------------------------------------------------

const contactEntrySchema = z.object({
  id: uuidSchema,
  fullName: z.string().nullish(),
  phone: z.string().nullish(),
  phoneAlt: z.string().nullish(),
  email: z.string().nullish(),
  address: z.string().nullish(),
  note: z.string().nullish(),
  regionId: uuidSchema.nullish(),
  regionName: z.string().nullish(),
  districtId: uuidSchema.nullish(),
  districtName: z.string().nullish(),
})

/**
 * `GET /api/subscribers/{id}/contact` javobi — `SubscriberContactView`.
 *
 * ⚠️ Kontakt YO'Q holati `404` emas, `200 {present:false}`. Shuning
 * uchun «yo'q» ni xato tutuvchidan emas, AYNAN shu bayroqdan o'qiymiz —
 * aks holda haqiqiy xato ham «kontakt yo'q» bo'lib ko'rinardi.
 */
const subscriberContactSchema = z.object({
  present: z.boolean(),
  contact: contactEntrySchema.nullish(),
})

export interface SubscriberContact {
  id: string
  fullName: string | null
  /** ⚠️ NIQOBLANGAN bo'lishi mumkin (`+9989****567`) — `ContactEntry.masked()` */
  phone: string | null
  phoneAlt: string | null
  email: string | null
  address: string | null
  note: string | null
  regionId: string | null
  districtId: string | null
  /**
   * ⚠️ Reestrdan olingan NOM — usiz `geo.read` yopiq bo'lgan sherikda
   * tanlov ro'yxati bo'sh qoladi va maydonda xom UUID ko'rinardi.
   */
  regionName: string | null
  districtName: string | null
}

/**
 * Abonentning kontakti — `null` bo'lsa YO'Q yoki KO'RINMAYDI.
 *
 * ⚠️ 404 va 403 bir xil ishlanadi. 404 — kontakt yozilmagan; 403 —
 * sherikda `contact.read` yo'q (`PartnerKind.java` da bugun yo'q).
 * Ikkalasida ham panel bazada nima turganini BILMAYDI, va aynan shu
 * bilim yo'qligi tahrirni cheklaydi: bilmagan narsani almashtirib
 * bo'lmaydi.
 *
 * ⚠️ Qolgan xatolar YUQORIGA o'tadi — 500 ni «kontakt yo'q» deb
 * ko'rsatish jimgina yolg'on bo'lardi.
 */
export async function fetchSubscriberContact(
  subscriberId: string,
  signal: AbortSignal,
): Promise<SubscriberContact | null> {
  try {
    // ⛔ `/api/contacts/**` EMAS — u xodimlar API'si va sherikka yopiq
    // (`contactDraft.ts` da sabab bilan yozilgan). Bu yo'l doirani
    // o'zi tekshiradi: begona abonent uchun 403, o'ziniki uchun 200.
    const response = await api.get<unknown>(`/api/subscribers/${subscriberId}/contact`, {
      signal,
    })
    const wrapper = subscriberContactSchema.parse(response.data)
    if (!wrapper.present || wrapper.contact === null || wrapper.contact === undefined) {
      return null
    }
    const raw = wrapper.contact
    return {
      id: raw.id,
      fullName: raw.fullName ?? null,
      phone: raw.phone ?? null,
      phoneAlt: raw.phoneAlt ?? null,
      email: raw.email ?? null,
      address: raw.address ?? null,
      note: raw.note ?? null,
      regionId: raw.regionId ?? null,
      districtId: raw.districtId ?? null,
      regionName: raw.regionName ?? null,
      districtName: raw.districtName ?? null,
    }
  } catch (error) {
    const status = axios.isAxiosError(error) ? error.response?.status : undefined
    // ⛔ `403` ENDI YUTILMAYDI. Eski yo'lda («xodimlar API'si») u
    // «huquqing yo'q» degani edi va uni «kontakt yo'q» deb ko'rsatish
    // zararsiz ko'rinardi. Yangi yo'lda esa `403` — «bu abonent seniki
    // EMAS», va uni `null` ga aylantirish xavfli: forma bo'sh ochilib,
    // saqlashda `PUT` TO'LIQ ALMASHTIRISH bilan mavjud kontaktni
    // o'chirib yuborardi.
    if (status === 404) return null
    throw error
  }
}

/**
 * `contactRequest()` yasagan so'rovni yuboradi.
 *
 * ⚠️ Yo'l va tana BU YERDA QURILMAYDI — ular `contactDraft.ts` da.
 * Ikki joyda qurilsa, sim shakli o'zgargan kuni bittasi eskirib qolardi.
 */
export async function sendContact(request: ContactRequest): Promise<void> {
  if (request.method === 'post') {
    await api.post<unknown>(request.path, request.body)
    return
  }
  await api.put<unknown>(request.path, request.body)
}

// --- Amallar ----------------------------------------------------------------
//
// Hammasi `OperationResult` qaytaradi va HTTP 200 bilan keladi — hatto
// `PARTIAL` bo'lganda ham. Ya'ni bu chaqiruvlar `catch` da emas, NATIJA
// sifatida ko'rsatiladi (`shared/api/operationResult.ts`).

async function stateAction(
  id: string,
  action: string,
  reason?: string,
): Promise<OperationResult> {
  const response = await api.post<unknown>(
    `/api/subscribers/${id}/${action}`,
    reason === undefined ? undefined : { reason },
  )
  return operationResultSchema.parse(response.data)
}

/**
 * Abonentni faollashtiradi — `NEW` yoki `SUSPENDED` dan `ACTIVE` ga.
 *
 * ⚠️ HUQUQ `subscriber.update` va u RESELLER to'plamida BOR
 * (`PartnerKind.java`), doira esa serverda qayta tekshiriladi
 * (`SubscriberController.activate` → `requireInScope`). Ya'ni bu amal
 * sherikka ilgari ham ochiq edi, faqat panelda tugmasi yo'q edi.
 *
 * ⚠️ `BLOCKED` DA CHAQIRILMAYDI. Server uni ataylab rad etadi
 * (`200 + FAILED`, «Abonent operator tomonidan bloklangan»): blokni
 * ochish alohida huquq ostidagi intizomiy amal
 * (`SubscriberServiceImpl.activate`). Chaqiruvchi tugmani o'sha holatda
 * umuman ko'rsatmaydi.
 *
 * ⚠️ BALANS SHARTI YO'Q va panel uni O'ZIDAN QO'SHMAYDI: server faqat
 * `BLOCKED` ni tekshiradi. Bo'sh balans — ogohlantirish sababi
 * (`SubscriberActionDialog`), to'siq emas.
 */
export function activateSubscriber(id: string): Promise<OperationResult> {
  return stateAction(id, 'activate')
}

export function blockSubscriber(id: string, reason?: string): Promise<OperationResult> {
  return stateAction(id, 'block', reason)
}

export function unblockSubscriber(id: string): Promise<OperationResult> {
  return stateAction(id, 'unblock')
}

/**
 * Hisobni yopish.
 *
 * ⚠️ `DELETE /api/subscribers/{id}` (haqiqiy o'chirish) SHU MODULDA
 * UMUMAN YO'Q, garchi `subscriber.delete` huquqi ikkalasini ham ochsa
 * ham. Yopish qaytarib bo'ladigan va izi qoladigan amal; o'chirish esa
 * ledger yozuvlari ishora qiladigan qatorni yo'q qiladi va reseller
 * uchun undan foyda yo'q — bitta noto'g'ri bosishning narxi esa
 * tiklanmaydigan.
 */
export function closeSubscriber(id: string, reason?: string): Promise<OperationResult> {
  return stateAction(id, 'close', reason)
}

/**
 * Parol o'rnatadi. QAYTARILGAN JAVOBDA PAROL YO'Q va u ekranda hech
 * qachon ko'rsatilmaydi (`CLAUDE.md` §7): o'qish endpointi backendda
 * ham ataylab mavjud emas.
 */
export async function setPassword(id: string, password: string): Promise<OperationResult> {
  const response = await api.put<unknown>(`/api/subscribers/${id}/password`, { password })
  return operationResultSchema.parse(response.data)
}

/**
 * Tezlik cheklovini qo'yadi yoki OLIB TASHLAYDI (`null`).
 *
 * ⚠️ `null` va `""` bir xil emas: bo'sh satr «tezlik yo'q» degan
 * qiymat bo'lib yozilishi mumkin, `null` esa «ustki qiymat yo'q,
 * tarifniki ishlasin» degani. Chaqiruvchi bo'sh maydonni `null` ga
 * aylantiradi.
 */
export async function setRateLimit(
  id: string,
  rateLimit: string | null,
): Promise<OperationResult> {
  const response = await api.put<unknown>(`/api/subscribers/${id}/rate-limit`, { rateLimit })
  return operationResultSchema.parse(response.data)
}

/**
 * Abonentning BARCHA qurilmalarini uzadi (`xsib-session`).
 *
 * ⚠️ Nishon `login`, `id` EMAS: endpoint RADIUS `username` bo'yicha
 * ishlaydi (`SessionControlResource.disconnectUser`).
 *
 * ⚠️ Bu amal bazani O'ZGARTIRMAYDI (`CLAUDE.md` §5.5) — u faqat
 * tarmoqdagi sessiyani uzadi. Shuning uchun natijasi ko'pincha
 * `PARTIAL` bo'ladi (qurilma javob bermadi) va bu XATO emas.
 */
export async function disconnectSessions(login: string): Promise<OperationResult> {
  const response = await api.post<unknown>(
    `/api/sessions/user/${encodeURIComponent(login)}/disconnect`,
  )
  return operationResultSchema.parse(response.data)
}

// --- Paket berish -----------------------------------------------------------

const grantIssueSchema = z.object({
  id: uuidSchema,
  planId: uuidSchema.nullish(),
  expiresAt: instantSchema.nullish(),
  /**
   * ⚠️ `true` — bu so'rov AVVALGISINING TAKRORI: paket ham, pul yozuvi
   * ham o'sha. `.default(false)` ataylab: eski backend maydonni umuman
   * yubormaydi va u yerda takror tushunchasi ham yo'q edi.
   */
  replay: z.boolean().default(false),
})

export interface IssuedGrant {
  id: string
  planId: string | null
  expiresAt: string | null
}

export type GrantOutcome =
  | { kind: 'ISSUED'; grant: IssuedGrant }
  /** Server takrorni tanidi — ikkinchi paket berilmadi, pul ikki marta yechilmadi */
  | { kind: 'REPLAY'; grant: IssuedGrant }
  /**
   * ⚠️ `ISSUED` DAN BOSHQA HOLAT va boshqa xabar. Pul ABONENTNING
   * hisobidan yechiladi va yetmaslik `422` bilan keladi
   * (`GrantController.issue`). Bu texnik xato EMAS: reseller
   * abonentning balansini to'ldirishini so'raydi yoki arzonroq paket
   * tanlaydi.
   */
  | { kind: 'INSUFFICIENT_FUNDS'; serverText: string | null }

function insufficientFunds(error: unknown): string | null | false {
  if (!axios.isAxiosError(error) || error.response?.status !== 422) return false
  const body: unknown = error.response.data
  if (body && typeof body === 'object' && 'error' in body) {
    const envelope = (body as { error?: { text?: unknown } | null }).error
    if (envelope && typeof envelope.text === 'string') return envelope.text
  }
  return null
}

/**
 * Paket beradi — pul ABONENTNING o'z balansidan yechiladi.
 *
 * <h2>⚠️ `Idempotency-Key` MAJBURIY</h2>
 * `POST /api/grants` kalitsiz so'rovni **400** bilan rad etadi; ayni
 * kalit bilan takror **200 + `replay:true`** qaytaradi; band kalit
 * boshqa amal bilan kelsa **409 `IDEMPOTENCY_CONFLICT`**.
 *
 * ⚠️ Kalit TASHQARIDAN keladi va bu shu imzoning sababi. U SO'ROV
 * uchun emas, AMAL uchun yasaladi: tugma qayta bosilsa yoki tarmoq
 * uzilib urinish takrorlansa u O'ZGARMASLIGI shart — aks holda server
 * ikkinchi so'rovni yangi sotuv deb qabul qiladi va abonentdan ikki
 * marta pul yechiladi (`docs/02-LEDGER.md` §6). Kalitning umri
 * `GrantIssueModal` da.
 *
 * ⚠️ `fundedBy: 'SUBSCRIBER'` va `charge: true` — QOTIRILGAN. `NONE`
 * (sovg'a) resellerga ochilmaydi: u pul yozuvisiz xizmat berish
 * demak, ya'ni hisob-kitobdan tashqarida qolgan trafik.
 */
export async function issueGrant(
  subscriberId: string,
  planId: string,
  idempotencyKey: string,
): Promise<GrantOutcome> {
  try {
    const response = await api.post<unknown>(
      '/api/grants',
      { subscriberId, planId, source: 'PLAN', fundedBy: 'SUBSCRIBER', charge: true },
      { headers: { 'Idempotency-Key': idempotencyKey } },
    )
    const raw = grantIssueSchema.parse(response.data)
    const grant: IssuedGrant = {
      id: raw.id,
      planId: raw.planId ?? null,
      expiresAt: raw.expiresAt ?? null,
    }
    return { kind: raw.replay ? 'REPLAY' : 'ISSUED', grant }
  } catch (error) {
    const text = insufficientFunds(error)
    // `false` — 422 emas, ya'ni haqiqiy nosozlik (400, 409, 5xx, tarmoq).
    // U YUQORIGA o'tadi va `translateError` uni tag bo'yicha tarjima
    // qiladi — jumladan `IDEMPOTENCY_CONFLICT` ni.
    if (text === false) throw error
    return { kind: 'INSUFFICIENT_FUNDS', serverText: text }
  }
}
